/**
 * Host billing: the organizations' plans and invoices, and the host's own mail and PayPal accounts.
 * Called from a host operator's own session; the server refuses all of it without Manage billing.
 */
import type {
  HostBillingRunResultDto,
  HostBillingSettingsDto,
  HostInvoiceDto,
  HostInvoiceListDto,
  HostTenantBillingDto,
  InvoiceStatus,
  MailAccountDto,
  MailTestRequest,
  MailTestResult,
  PayPalAccountDto,
  PayPalTestRequest,
  PayPalTestResult,
  UpdateHostBillingSettingsRequest,
  UpdateHostTenantBillingRequest,
  UpdateMailAccountRequest,
  UpdatePayPalAccountRequest
} from '../contracts.gen.js';
import { describeError, request } from './http.js';

const BASE = '/api/host/billing';

export function settings(signal?: AbortSignal): Promise<HostBillingSettingsDto> {
  return request<HostBillingSettingsDto>(`${BASE}/settings`, signal ? { signal } : {});
}

export function updateSettings(body: UpdateHostBillingSettingsRequest): Promise<HostBillingSettingsDto> {
  return request<HostBillingSettingsDto>(`${BASE}/settings`, { method: 'PUT', body });
}

export function run(): Promise<HostBillingRunResultDto> {
  return request<HostBillingRunResultDto>(`${BASE}/run`, { body: {} });
}

export function plans(signal?: AbortSignal): Promise<HostTenantBillingDto[]> {
  return request<HostTenantBillingDto[]>(`${BASE}/plans`, signal ? { signal } : {});
}

/** Switching a plan on after its trial ran out also resumes the organization and raises its first
 * invoice; anything about that which did not work comes back as a warning beside the plan. */
export async function updatePlan(
  tenantId: number,
  body: UpdateHostTenantBillingRequest
): Promise<{ plan: HostTenantBillingDto; warning: string | null }> {
  const answer = await request<HostTenantBillingDto | { plan: HostTenantBillingDto; warning: string }>(`${BASE}/plans/${tenantId}`, {
    method: 'PUT',
    body
  });
  return 'plan' in answer ? { plan: answer.plan, warning: answer.warning } : { plan: answer, warning: null };
}

/** Starts the organization's free trial; `days` null uses the host's default length. */
export function startTrial(tenantId: number, days: number | null, notify: boolean): Promise<HostTenantBillingDto> {
  return request<HostTenantBillingDto>(`${BASE}/plans/${tenantId}/trial`, { body: { days, notify } });
}

/** Ends a running trial now, as reaching its last day would. */
export function endTrial(tenantId: number): Promise<HostTenantBillingDto> {
  return request<HostTenantBillingDto>(`${BASE}/plans/${tenantId}/trial/end`, { body: {} });
}

/** An invoice raised but not emailed comes back with the reason beside it. */
export interface InvoiceWithWarning {
  invoice: HostInvoiceDto;
  warning: string | null;
}

export function billNow(tenantId: number, send: boolean): Promise<InvoiceWithWarning> {
  return request<InvoiceWithWarning>(`${BASE}/plans/${tenantId}/bill`, { body: { send } });
}

export interface InvoiceFilter {
  tenantId?: number | null;
  status?: InvoiceStatus | null;
  overdue?: boolean;
}

export function invoices(filter: InvoiceFilter = {}, signal?: AbortSignal): Promise<HostInvoiceListDto> {
  const q = new URLSearchParams();
  if (filter.tenantId != null) q.set('tenantId', String(filter.tenantId));
  if (filter.overdue) q.set('overdue', 'true');
  else if (filter.status != null) q.set('status', String(filter.status));
  const qs = q.toString();
  return request<HostInvoiceListDto>(`${BASE}/invoices${qs ? '?' + qs : ''}`, signal ? { signal } : {});
}

export function invoice(id: number, signal?: AbortSignal): Promise<HostInvoiceDto> {
  return request<HostInvoiceDto>(`${BASE}/invoices/${id}`, signal ? { signal } : {});
}

export function pdfPath(id: number): string {
  return `${BASE}/invoices/${id}/pdf`;
}

export function issue(id: number, email: boolean): Promise<InvoiceWithWarning> {
  return request<InvoiceWithWarning>(`${BASE}/invoices/${id}/issue?email=${email}`, { body: {} });
}

export function send(id: number): Promise<{ invoice: HostInvoiceDto; success: boolean; message: string }> {
  return request(`${BASE}/invoices/${id}/send`, { body: {} });
}

export function markPaid(id: number, note: string | null, sendReceipt: boolean): Promise<HostInvoiceDto> {
  return request<HostInvoiceDto>(`${BASE}/invoices/${id}/paid`, { body: { note, sendReceipt } });
}

export function voidInvoice(id: number): Promise<HostInvoiceDto> {
  return request<HostInvoiceDto>(`${BASE}/invoices/${id}/void`, { body: {} });
}

/** Ends the plan; service continues to the end of the last period invoiced. */
export function terminate(tenantId: number, reason: string | null, notify: boolean): Promise<HostTenantBillingDto> {
  return request<HostTenantBillingDto>(`${BASE}/plans/${tenantId}/terminate`, { body: { reason, notify } });
}

export function reinstate(tenantId: number): Promise<HostTenantBillingDto> {
  return request<HostTenantBillingDto>(`${BASE}/plans/${tenantId}/reinstate`, { body: {} });
}

// ---- the host's accounts, shaped for the shared Settings cards ----

export const mailApi = {
  get: (signal?: AbortSignal): Promise<MailAccountDto> => request<MailAccountDto>(`${BASE}/mail`, signal ? { signal } : {}),
  update: (body: UpdateMailAccountRequest): Promise<MailAccountDto> => request<MailAccountDto>(`${BASE}/mail`, { method: 'PUT', body }),
  /** Never throws: a test that could not run is a failed test. */
  async test(req: MailTestRequest): Promise<MailTestResult> {
    try {
      return await request<MailTestResult>(`${BASE}/mail/test`, {
        body: { settings: req.settings ?? null, sendTestTo: req.sendTestTo ?? null }
      });
    } catch (error) {
      const skipped = { checked: false, ok: false, detail: '' };
      return { success: false, smtp: skipped, pop3: skipped, summary: 'The test could not be run: ' + describeError(error) };
    }
  }
};

export const payPalApi = {
  get: (signal?: AbortSignal): Promise<PayPalAccountDto> => request<PayPalAccountDto>(`${BASE}/paypal`, signal ? { signal } : {}),
  update: (body: UpdatePayPalAccountRequest): Promise<PayPalAccountDto> =>
    request<PayPalAccountDto>(`${BASE}/paypal`, { method: 'PUT', body }),
  async test(req: PayPalTestRequest): Promise<PayPalTestResult> {
    try {
      return await request<PayPalTestResult>(`${BASE}/paypal/test`, { body: req });
    } catch (error) {
      return { success: false, message: 'The test could not be run: ' + describeError(error) };
    }
  }
};
